import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, X, FileText, Image, File, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useGeneratePresignedUrl, uploadFileToR2 } from "@/hooks/useMedia";

export interface UploadedFile {
  key: string;
  fileName: string;
  mimeType: string;
  size: number;
}

interface FileUploadProps {
  onFilesChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxSizeInMB?: number;
  accept?: string;
  value?: UploadedFile[]; // Adicionar prop para controlar externamente
}

export const FileUpload = ({
  onFilesChange,
  maxFiles = 5,
  maxSizeInMB = 10,
  accept = "*/*",
  value,
}: FileUploadProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generatePresignedUrl = useGeneratePresignedUrl();

  // Sincronizar com valor externo (quando parent limpa os attachments)
  useEffect(() => {
    if (value !== undefined && value.length === 0 && files.length > 0) {
      setFiles([]);
    }
  }, [value]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (files.length + selectedFiles.length > maxFiles) {
      toast.error(`Você pode anexar no máximo ${maxFiles} arquivos.`);
      return;
    }

    // Validar tamanho dos arquivos
    const invalidFiles = selectedFiles.filter(
      (file) => file.size > maxSizeInMB * 1024 * 1024,
    );

    if (invalidFiles.length > 0) {
      toast.error(
        `Alguns arquivos excedem o tamanho máximo de ${maxSizeInMB}MB.`,
      );
      return;
    }

    setUploading(true);

    try {
      const uploadedFiles: UploadedFile[] = await Promise.all(
        selectedFiles.map(async (file) => {
          try {
            // 1. Determinar o tipo baseado no MIME type
            const fileType: "IMAGE" | "DOCUMENT" = file.type.startsWith("image/")
              ? "IMAGE"
              : "DOCUMENT";

            // 2. Solicitar presigned URL ao backend com o formato correto
            const presignedData = await generatePresignedUrl.mutateAsync({
              fileName: file.name,
              mimeType: file.type,
              type: fileType,
              size: file.size,
            });

            // 3. Fazer upload direto para R2 usando presigned URL
            await uploadFileToR2(presignedData.uploadUrl, file);

            // 4. Retornar objeto de attachment com a key fornecida pelo backend
            return {
              key: presignedData.key,
              fileName: file.name,
              mimeType: file.type,
              size: file.size,
            };
          } catch (error) {
            console.error("Erro ao fazer upload do arquivo:", file.name, error);
            throw error;
          }
        }),
      );

      const newFiles = [...files, ...uploadedFiles];
      setFiles(newFiles);
      onFilesChange(newFiles);
      toast.success("Arquivos anexados com sucesso!");
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error("Erro ao anexar arquivos. Tente novamente.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFilesChange(newFiles);
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) {
      return <Image className="h-4 w-4" />;
    } else if (mimeType.includes("pdf")) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading || files.length >= maxFiles}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || files.length >= maxFiles}
          className="cursor-pointer"
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Paperclip className="h-4 w-4 mr-2" />
              Anexar Arquivos
            </>
          )}
        </Button>
        <span className="text-xs text-gray-500">
          {files.length}/{maxFiles} arquivos
        </span>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {getFileIcon(file.mimeType)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {file.fileName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFile(index)}
                className="h-7 w-7 p-0 cursor-pointer"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
